import { Agent, AgentCategory } from "../types";

export const categoryColors: Record<AgentCategory, string> = {
  "Business Foundations": "bg-pink-100 text-pink-700",
  "AI Tool Use": "bg-violet-100 text-violet-700",
  LinkedIn: "bg-yellow-100 text-yellow-700",
  "Content Creation": "bg-red-100 text-red-700",
  "Lead Generation": "bg-orange-100 text-orange-700",
  Sales: "bg-blue-100 text-blue-700",
  "Project Management": "bg-green-100 text-green-700",
};

export const categoryIconColors: Record<
  AgentCategory,
  { bg: string; text: string; gradient: string }
> = {
  "Business Foundations": {
    bg: "bg-gradient-to-br from-pink-100 via-pink-50 to-rose-100",
    text: "text-pink-600",
    gradient: "from-pink-500 to-rose-500",
  },
  "AI Tool Use": {
    bg: "bg-gradient-to-br from-violet-100 via-purple-50 to-indigo-100",
    text: "text-violet-600",
    gradient: "from-violet-500 to-indigo-500",
  },
  LinkedIn: {
    bg: "bg-gradient-to-br from-amber-100 via-yellow-50 to-orange-100",
    text: "text-amber-600",
    gradient: "from-amber-500 to-orange-500",
  },
  "Content Creation": {
    bg: "bg-gradient-to-br from-red-100 via-rose-50 to-pink-100",
    text: "text-red-600",
    gradient: "from-red-500 to-pink-500",
  },
  "Lead Generation": {
    bg: "bg-gradient-to-br from-orange-100 via-amber-50 to-yellow-100",
    text: "text-orange-600",
    gradient: "from-orange-500 to-amber-500",
  },
  Sales: {
    bg: "bg-gradient-to-br from-blue-100 via-sky-50 to-cyan-100",
    text: "text-blue-600",
    gradient: "from-blue-500 to-cyan-500",
  },
  "Project Management": {
    bg: "bg-gradient-to-br from-emerald-100 via-green-50 to-teal-100",
    text: "text-emerald-600",
    gradient: "from-emerald-500 to-teal-500",
  },
};

export const categories: AgentCategory[] = [
  "Business Foundations",
  "AI Tool Use",
  "LinkedIn",
  "Sales",
];

export const agents: Agent[] = [
  {
    id: "onboarding-guide",
    name: "Getting Started",
    description:
      "Your personal onboarding assistant that walks you through setting up your AI consulting business. Captures your background, skills, goals, and niche preferences to personalize your Stack experience.",
    category: "Business Foundations",
    iconName: "Rocket",
    prompt:
      "Let's get started with my onboarding.",
    isOnboarding: true,
  },
  {
    id: "niche-finder",
    name: "Niche Finder",
    description:
      "A guided strategist that interviews you about your background, goals, and network to identify your most profitable AI consulting niche. Generates a scored shortlist of tailored opportunities and delivers a 30-day validation plan.",
    category: "Business Foundations",
    iconName: "Crosshair",
    prompt: "Help me discover and validate my ideal AI consulting niche",
  },
  {
    id: "skills-inventory-analyst",
    name: "Skills Inventory Analyst",
    description:
      "Helps members inventory and organize their professional skills, experience, and expertise to identify strengths and gaps for their AI consulting business.",
    category: "Business Foundations",
    iconName: "ChartBar",
    prompt:
      "Help me inventory and organize my professional skills and experience for AI consulting",
  },
  {
    id: "service-package-pricing",
    name: "Service, Package & Pricing Agent",
    description:
      "Stack's strategic offer designer that helps you structure your services, create compelling packages, and set pricing that reflects your value.",
    category: "Business Foundations",
    iconName: "CurrencyDollar",
    prompt:
      "Help me structure my services, create packages, and set pricing for my consulting business",
  },
  {
    id: "linkedin-profile-optimizer",
    name: "LinkedIn Profile Optimizer",
    description:
      "A profile optimization expert that helps professionals craft compelling LinkedIn profiles optimized for attracting AI consulting clients.",
    category: "LinkedIn",
    iconName: "User",
    prompt: "Optimize my LinkedIn profile to attract AI consulting clients",
  },
  {
    id: "linkedin-content-strategist",
    name: "LinkedIn Content Strategist",
    description:
      "A content architect that helps consultants plan and create engaging LinkedIn content to build authority and attract clients.",
    category: "LinkedIn",
    iconName: "PencilLine",
    prompt:
      "Help me plan and create engaging LinkedIn content to build my authority as a consultant",
  },
  {
    id: "client-discovery-call-planner",
    name: "Client Discovery Call Planner",
    description:
      "Designs high-quality discovery call frameworks to help you understand client needs, qualify opportunities, and set the stage for winning proposals.",
    category: "Sales",
    iconName: "Phone",
    prompt:
      "Help me design a discovery call framework for qualifying potential clients",
  },
  {
    id: "project-proposal-generator",
    name: "Project Proposal Generator",
    description:
      "Creates polished, persuasive client proposals in minutes. Guides you through scope, deliverables, pricing, and next steps with built-in client perspective analysis.",
    category: "Sales",
    iconName: "Scroll",
    prompt: "Help me create a professional project proposal for a client",
  },
  {
    id: "service-agreement-generator",
    name: "Service Agreement Generator",
    description:
      "Generates professional service agreements and contracts tailored to your consulting engagements, covering scope, terms, and deliverables.",
    category: "Sales",
    iconName: "FileText",
    prompt:
      "Help me generate a professional service agreement for my consulting engagement",
  },
  {
    id: "prompt-optimizer",
    name: "Prompt Optimizer",
    description:
      "Transforms rough, vague, or incomplete prompts into clear, effective instructions that get better results from AI tools.",
    category: "AI Tool Use",
    iconName: "Lightning",
    prompt:
      "Help me optimize my prompts to get better results from AI tools",
  },
  {
    id: "n8n-workflow-architect",
    name: "n8n Workflow Architect",
    description:
      "An intelligent automation architect specializing in n8n workflow design. Helps you plan, structure, and build automation workflows for your consulting practice.",
    category: "AI Tool Use",
    iconName: "Wrench",
    prompt:
      "Help me design and build an n8n automation workflow for my consulting practice",
  },
  {
    id: "toolkit-selector",
    name: "Toolkit Selector",
    description:
      "A strategic tool matcher that interviews you about your skills, goals, and niche to recommend a personalized toolkit of 3 complementary tools from the Stack Tool Directory.",
    category: "AI Tool Use",
    iconName: "Toolbox",
    prompt:
      "Help me identify the best toolkit of 3 tools for my consulting business",
  },
];
